import * as THREE from 'three';
import { Subscription } from 'rxjs';
import { ICharacterAI } from '../../interfaces/ICharacterAI';
import { Character } from '../Character';
import { PathNode } from '../../world/PathNode';
import { WalkOnPath } from './WalkOnPath';
import { NavMeshAgent } from './NavMeshAgent';
import { NavMesh } from '../../world/NavMesh';
import { PanicRun } from '../states/PanicRun';
import { IdleReposition } from '../states/IdleReposition';

type Phase = 'patrolling' | 'watching' | 'panicking' | 'repositioning';

export class PedestrianBehavior implements ICharacterAI {
    public character: Character;

    private static readonly CRIME_RADIUS = 20;
    private static readonly PLAYER_STOP_RADIUS = 2;
    private static readonly TOUCH_RADIUS = 1;
    private static readonly AVOIDANCE_RADIUS = 3;
    private static readonly REPOSITION_DURATION = 2.5;
    private static readonly WATCH_MIN = 2;
    private static readonly WATCH_MAX = 3;
    private static readonly WATCH_COOLDOWN = 4;
    private static readonly PATH_NODE_RADIUS = 1;

    private phase: Phase = 'patrolling';
    private walkOnPath: WalkOnPath | null = null;
    private navMeshAgent: NavMeshAgent | null = null;
    private avoidanceSideRight: boolean | null = null;
    private watchTimer = 0;
    private watchDuration = 0;
    private watchCooldown = 0;
    private repositionTimer = 0;
    private panicking = false;
    private crimeSubscription: Subscription | null = null;
    private initialized = false;

    constructor(private readonly firstNode?: PathNode, private readonly navMesh?: NavMesh) {}

    public update(timeStep: number): void {
        if (!this.character) { return; }
        if (!this.initialized) { this.init(); }

        switch (this.phase) {
            case 'panicking':     this.updatePanicking(); break;
            case 'watching':      this.updateWatching(timeStep); break;
            case 'repositioning': this.updateRepositioning(timeStep); break;
            default:              this.updatePatrolling(timeStep); break;
        }
    }

    public destroy(): void {
        this.crimeSubscription?.unsubscribe();
        this.crimeSubscription = null;
    }

    private init(): void {
        this.initialized = true;

        if (this.navMesh) {
            this.navMeshAgent = new NavMeshAgent(this.navMesh);
            this.navMeshAgent.character = this.character;
        } else if (this.firstNode) {
            this.walkOnPath = new WalkOnPath(this.firstNode, PedestrianBehavior.PATH_NODE_RADIUS);
            this.walkOnPath.character = this.character;
        }

        const service = this.character.world?.crimeDetectionService;
        if (service) {
            this.crimeSubscription = service.crime$.subscribe(event => {
                if (this.panicking || this.character.isDead) { return; }
                const npcPos = this.character.getWorldPosition(new THREE.Vector3());
                if (event.position.distanceTo(npcPos) <= PedestrianBehavior.CRIME_RADIUS) {
                    this.startPanic();
                }
            });
        }
    }

    private updatePatrolling(timeStep: number): void {
        const player = this.character.world?.player;
        let npcPos: THREE.Vector3 | null = null;
        let playerPos: THREE.Vector3 | null = null;

        if (player && !player.isDead) {
            npcPos = this.character.getWorldPosition(new THREE.Vector3());
            playerPos = player.getWorldPosition(new THREE.Vector3());
            const dist = npcPos.distanceTo(playerPos);

            if (dist < PedestrianBehavior.TOUCH_RADIUS) {
                this.startReposition();
                return;
            }

            if (this.watchCooldown > 0) {
                this.watchCooldown -= timeStep;
            } else {
                const toPlayer = playerPos.clone().sub(npcPos).setY(0).normalize();
                const npcForward = new THREE.Vector3(0, 0, 1).applyQuaternion(this.character.quaternion);
                if (npcForward.dot(toPlayer) > 0 && dist <= PedestrianBehavior.PLAYER_STOP_RADIUS) {
                    this.startWatching();
                    return;
                }
            }
        }

        if (this.navMeshAgent) {
            this.navMeshAgent.update(timeStep);
            if (npcPos && playerPos) {
                this.applyPlayerAvoidance(npcPos, playerPos);
            }
        } else if (this.walkOnPath) {
            this.walkOnPath.update(timeStep);
            if (npcPos && playerPos) {
                this.applyPlayerAvoidance(npcPos, playerPos);
            }
        }
    }

    private applyPlayerAvoidance(npcPos: THREE.Vector3, playerPos: THREE.Vector3): void {
        const toPlayer = playerPos.clone().sub(npcPos).setY(0);
        const dist = toPlayer.length();
        if (dist >= PedestrianBehavior.AVOIDANCE_RADIUS) {
            this.avoidanceSideRight = null;
            return;
        }

        const toPlayerNorm = toPlayer.clone().normalize();
        const npcForward = new THREE.Vector3(0, 0, 1).applyQuaternion(this.character.quaternion);
        if (npcForward.dot(toPlayerNorm) <= 0) {
            this.avoidanceSideRight = null;
            return;
        }

        // Right-perpendicular to the to-player direction in the horizontal plane
        const up = new THREE.Vector3(0, 1, 0);
        const right = new THREE.Vector3().crossVectors(toPlayerNorm, up).normalize();

        // Commit to a side only once per avoidance encounter to prevent oscillation
        if (this.avoidanceSideRight === null) {
            this.avoidanceSideRight = npcForward.dot(right) >= 0;
        }
        const steerSide = this.avoidanceSideRight ? right.clone() : right.clone().negate();

        // Avoidance is stronger the closer the player is
        const avoidStrength = 1 - dist / PedestrianBehavior.AVOIDANCE_RADIUS;
        const blended = npcForward.clone().add(steerSide.multiplyScalar(avoidStrength * 2)).normalize();

        this.character.setViewVector(blended);
        this.character.setOrientation(blended);
    }

    private updateRepositioning(timeStep: number): void {
        this.repositionTimer += timeStep;
        if (this.repositionTimer >= PedestrianBehavior.REPOSITION_DURATION) {
            this.phase = 'patrolling';
            this.repositionTimer = 0;
        }
    }

    private updateWatching(timeStep: number): void {
        this.character.triggerAction('up', false);

        const player = this.character.world?.player;
        if (player) {
            const npcPos  = this.character.getWorldPosition(new THREE.Vector3());
            const toPlayer = player.getWorldPosition(new THREE.Vector3()).sub(npcPos);
            this.character.setOrientation(toPlayer);
        }

        this.watchTimer += timeStep;
        if (this.watchTimer >= this.watchDuration) {
            this.phase = 'patrolling';
            this.watchTimer = 0;
            this.watchCooldown = PedestrianBehavior.WATCH_COOLDOWN;
        }
    }

    private updatePanicking(): void {
        if (this.navMeshAgent) {
            this.navMeshAgent.update(0);
            const targetPos = this.navMeshAgent.currentTargetPosition;
            if (targetPos) {
                const npcPos = this.character.getWorldPosition(new THREE.Vector3());
                this.character.setOrientation(targetPos.clone().sub(npcPos));
            }
        } else if (this.walkOnPath) {
            this.walkOnPath.update(0);
            // WalkFollowTarget only calls setOrientation at node arrival; drive it every frame
            const npcPos = this.character.getWorldPosition(new THREE.Vector3());
            const targetPos = this.walkOnPath.target.getWorldPosition(new THREE.Vector3());
            this.character.setOrientation(targetPos.sub(npcPos));
        } else {
            // No path — flee directly away from the player
            const player = this.character.world?.player;
            if (player) {
                const npcPos = this.character.getWorldPosition(new THREE.Vector3());
                const away = npcPos.sub(player.getWorldPosition(new THREE.Vector3()));
                this.character.setOrientation(away);
            }
        }
    }

    private startReposition(): void {
        this.phase = 'repositioning';
        console.log('Pedestrian repositioning');
        this.repositionTimer = 0;
        this.character.setState(new IdleReposition(this.character));
    }

    private startWatching(): void {
        this.phase = 'watching';
        this.watchTimer = 0;
        this.watchDuration = PedestrianBehavior.WATCH_MIN
            + Math.random() * (PedestrianBehavior.WATCH_MAX - PedestrianBehavior.WATCH_MIN);
        this.character.triggerAction('up', false);
    }

    private startPanic(): void {
        this.panicking = true;
        this.phase = 'panicking';
        if (this.navMeshAgent) { this.navMeshAgent.reverse = true; }
        else if (this.walkOnPath) { this.walkOnPath.reverse = true; }
        this.character.setState(new PanicRun(this.character));
    }
}
